import { useQuery } from '@tanstack/react-query';
import {  } from 'lucide-react';
import type { LeadStatus } from '@conversia/shared-types';
import * as conversationsApi from '@/api/conversations.api';
import * as channelsApi from '@/api/channels.api';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConversationItem } from './ConversationItem';
import { HelpTip } from '@/components/ui/HelpTip';
import type { ConversationFilters } from '@/store/conversations.store';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ESQUEMA_CONVERSACIONES } from '@/lib/filtros-esquemas';
import { channelTypeLabel } from '@/lib/badges';
import { cn } from '@/lib/cn';
import { FiltrosGuardados } from '@/components/filtros/FiltrosGuardados';
import { CajaDeBusqueda } from '@/components/common/CajaDeBusqueda';
import { Cargando } from '@/components/common/Cargando';

// Pestañas por TEMPERATURA del lead (embudo) + "Atención" (alta A1 de la
// auditoría): las conversaciones en WAITING_HUMAN — a quién le debo respuesta
// YA — con filtro propio en vez de buscar bordes ámbar entre cien filas.
const ATTN_TAB = '__ATTN' as const;
const LEAD_TABS: { value: LeadStatus | '' | typeof ATTN_TAB; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: ATTN_TAB, label: 'Atención' },
  { value: 'CALIENTE', label: 'Caliente' },
  { value: 'TIBIO', label: 'Tibio' },
  { value: 'FRIO', label: 'Frío' },
  { value: 'CERRADO', label: 'Cerrado' },
  { value: 'NO_CERRADO', label: 'No cerrado' },
];

const TYPES: { value: 'all' | 'individual' | 'group'; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'individual', label: 'Individuales' },
  { value: 'group', label: 'Grupos' },
];

export function ConversationList({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  // A1: los filtros de esta pantalla viven en la URL. Recargar los conserva,
  // atras/adelante funcionan, y el enlace se puede compartir.
  const { filtros: filters, setFiltros } = useFiltrosUrl<ConversationFilters>(
    ESQUEMA_CONVERSACIONES,
  );

  const { data: channels } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['conversations', 'list', filters],
    queryFn: () =>
      conversationsApi.listConversations({
        sinAtender: filters.sinAtender || undefined,
        status: filters.status,
        leadStatus: filters.leadStatus,
        channelId: filters.channelId,
        search: filters.search || undefined,
        type: filters.type && filters.type !== 'all' ? filters.type : undefined,
        take: 100,
      }),
  });

  // El orden lo manda la API: el último que escribió, arriba, como WhatsApp
  // (Kervens, 4-nov-2026). Antes aquí se subían los de «Atención» por encima
  // de todo; reordenar de nuevo en el navegador deshacía el orden por fecha
  // justo después de pedirlo, así que ya no se toca.
  const items = data?.items ?? [];

  return (
    <div className="flex h-full w-full flex-col border-r border-surface-border bg-white dark:border-white/10 dark:bg-slate-950 sm:w-88">
      <div className="space-y-2.5 border-b border-surface-border p-3 dark:border-white/10">
        {/* A2 · Filtros guardados. Esta lista sirve /conversations, asi que el
            componente lee esa ruta por su cuenta y guarda contra ella. */}
        <FiltrosGuardados />
        <div className="flex items-center gap-2">
          {/* A7 · La misma caja que en Clientes, Contactos y CRM. */}
          <CajaDeBusqueda
            valor={filters.search ?? ''}
            onChange={(v) => setFiltros({ search: v })}
            placeholder="Buscar contacto…"
            className="flex-1"
          />
          <HelpTip helpKey="conversations" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_TABS.map((s) => {
            const isAttn = s.value === ATTN_TAB;
            const active = isAttn
              ? filters.status === 'WAITING_HUMAN'
              : !filters.status && (filters.leadStatus ?? '') === s.value;
            return (
              <button
                key={s.value || 'all'}
                onClick={() =>
                  s.value === ATTN_TAB
                    ? setFiltros({ leadStatus: undefined, status: 'WAITING_HUMAN' })
                    : setFiltros({ leadStatus: s.value || undefined, status: undefined })
                }
                className={cn(
                  'min-h-8 rounded-full px-2.5 text-xs font-medium transition-colors lateral:min-h-6',
                  active
                    ? isAttn
                      ? 'bg-amber-500 text-white'
                      : 'bg-acento-lleno text-white'
                    : 'bg-[var(--mac-btn)] text-slate-900 hover:bg-[var(--mac-btn-pulsado)] dark:text-slate-100',
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <div className="flex gap-0.5 rounded-[9px] bg-[var(--mac-carril)] p-0.5 lateral:rounded-[7px]">
          {TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => setFiltros({ type: t.value })}
              className={cn(
                'min-h-8 flex-1 rounded-[7px] px-2.5 text-xs transition-colors lateral:min-h-5 lateral:rounded-[5px]',
                (filters.type ?? 'all') === t.value
                  ? 'bg-acento-lleno text-white'
                  : 'text-slate-900 hover:bg-black/5 dark:text-slate-100 dark:hover:bg-white/5',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={filters.channelId ?? ''}
          onChange={(e) => setFiltros({ channelId: e.target.value || undefined })}
          className="h-11 w-full rounded-[10px] border-0 bg-white px-2 text-base text-slate-900 shadow-[0_0_0_1px_var(--mac-campo-filo)] focus:outline-none focus:ring-[3.5px] focus:ring-acento/50 dark:bg-white/5 dark:text-slate-100 lateral:h-6 lateral:rounded-[5px] lateral:text-sm"
        >
          <option value="">Todos los canales</option>
          {channels?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} · {channelTypeLabel[c.type]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <Cargando />
        ) : items.length === 0 ? (
          <EmptyState title="Sin conversaciones" description="No hay conversaciones con estos filtros." className="m-4" />
        ) : (
          <ul data-guia="conversations.lista" className="space-y-0.5 p-2">
            {items.map((c) => (
              <li key={c.id}>
                <ConversationItem
                  conversation={c}
                  selected={selectedId === c.id}
                  onSelect={() => onSelect(c.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
